import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  TemplateRef,
  viewChild
} from '@angular/core';

import { Observable, forkJoin, of } from 'rxjs';
import { finalize, switchMap } from 'rxjs/operators';

import { FormBuilder, FormGroup } from '@angular/forms';
import { Attestation, AttestationDataService } from '@sondermind/data-access/attestation';
import { CreditCardAddComponent } from '@sondermind/features/account-edit';
import { IrisButtonContainerAlignments, IrisButtonTypes } from '@sondermind/iris/button';
import { IrisButtonTypeAttributeOptions } from '@sondermind/iris/shared';

import { AccountSetupService } from '@sondermind/utilities/services';

import { InfoLinks } from '@sondermind/core/enums/info-links.enum';

@Component({
  selector: 'client-setup-payment-info',
  templateUrl: './setup-payment-info.component.html',
  styleUrls: ['./setup-payment-info.component.scss'],
  standalone: false
})
export class SetupPaymentInfoComponent {
  @Input() useInSessionRequest: boolean = false;
  @Input() isMobile: boolean = false;
  @Input() isProposeATime: boolean = false;
  @Input() showPaymentFaq: boolean = false;
  @Input() fromDirectBooking: boolean = false;
  @Output() paymentInfoSubmitted: EventEmitter<void> = new EventEmitter<void>();

  @ViewChild('ccForm') ccFormComponent: CreditCardAddComponent;

  // Template for the action button that parent component can render
  readonly actionButtonTemplate = viewChild.required<TemplateRef<unknown>>('actionButton');

  financialResponsibilityAgreement = InfoLinks.FINANCIAL_RESPONSIBILITY_AGREEMENT;

  submittingFinancialPolicy: boolean = false;
  IrisButtonContainerAlignments = IrisButtonContainerAlignments;
  IrisButtonTypes = IrisButtonTypes;
  IrisButtonTypeAttributeOptions = IrisButtonTypeAttributeOptions;
  paymentForm: FormGroup;
  constructor(
    private attestationDataService: AttestationDataService,
    private accountSetupService: AccountSetupService,
    private fb: FormBuilder
  ) {
    this.initializeForm();
  }

  initializeForm() {
    this.paymentForm = this.fb.group({
      acceptedFinancialPolicy: [false]
    });
  }

  /**
   * Enable submit button on new client setup when CC Form is complete, not currently
   * submitting, and client has checked box accepting financial policy
   */
  get allowSubmit(): boolean {
    return this.ccFormComponent && this.ccFormComponent.getStripeFormValidity()
      && !this.ccFormComponent.submitting && !this.submittingFinancialPolicy
      && this.paymentForm.value.acceptedFinancialPolicy;
  }

  /**
   * Submit child credit card form. Only credit card can be added during new client setup.
   * When form succeeds, event emitted triggers submitFinancialAgreements
   */
  submitCreditCard(): void {
    this.ccFormComponent.stripeForm.requestSubmit();
  }

  /*
   * Combine into list of attestations sign requests and return forkJoin of list.
   * Triggered after adding first credit card.
   */
  submitFinancialAgreements(): void {
    this.startSubmitting();

    this.attestationDataService.refresh().pipe(
      switchMap(
        (resp: Attestation[]) => {
          // filter to financial agreement only
          const filtered: Attestation[] = resp.filter((att) => att.agreedAt == null && att.documentTitle.includes('Financial'));
          const reqs: Array<Observable<void>> = filtered.map(
            (att) => this.attestationDataService.markAgreed(att.id, this.accountSetupService.context)
          );

          // if attestations have all been signed, dont forkJoin
          if (reqs.length === 0) {
            return of(undefined);
          }

          return forkJoin(reqs);
        }
      ),
      finalize(() => this.stopSubmitting())
    ).subscribe({
      next: () => {
        this.paymentInfoSubmitted.emit();
      },
      error: (error) => {
        // eslint-disable-next-line no-console
        console.error('Failed to submit financial agreements');
        // eslint-disable-next-line no-console
        console.error(error);
      }
    });
  }

  /**
   * Wrapper for unit tests.
   */
  startSubmitting(): void {
    this.submittingFinancialPolicy = true;
  }

  /**
   * Wrapper for unit tests.
   */
  stopSubmitting(): void {
    this.submittingFinancialPolicy = false;
  }
}
